import type {
  AdminAuction,
  AdminAuctionBid,
  AuctionIncrementTier,
  AuctionStatus,
} from '@nucleus-commerce/api-client/admin';

/** The backend's default (D-148): $50 under $1,000, $100 to $5,000, $250 to $10,000, $500 above. */
export const DEFAULT_INCREMENTS: readonly AuctionIncrementTier[] = [
  { fromCents: 0, incrementCents: 5_000 },
  { fromCents: 100_000, incrementCents: 10_000 },
  { fromCents: 500_000, incrementCents: 25_000 },
  { fromCents: 1_000_000, incrementCents: 50_000 },
];

export const DEFAULT_ANTI_SNIPE_MINUTES = 2;
export const MAX_INCREMENT_TIERS = 20;
export const MAX_DURATION_DAYS = 30;
export const REASON_MAX = 500;

/** The detail page polls this often while bidding is open; nothing changes otherwise. */
export const LIVE_POLL_MS = 5_000;

export const AUCTION_TABS = ['live', 'scheduled', 'draft', 'ended', 'cancelled'] as const;
export type AuctionTab = (typeof AUCTION_TABS)[number];

/** Times, prices, increments and premium change only before bidding opens. */
export function isEditable(auction: Pick<AdminAuction, 'status'>): boolean {
  return auction.status === 'draft' || auction.status === 'scheduled';
}

/** A scheduled or live auction withdraws its lot from the cart; one per store product. */
export function isActive(status: AuctionStatus): boolean {
  return status === 'scheduled' || status === 'live';
}

export function canCancel(auction: Pick<AdminAuction, 'status'>): boolean {
  return auction.status !== 'ended' && auction.status !== 'cancelled';
}

/** The `refetchInterval` of the detail and its bids: every few seconds while live, else never. */
export function pollInterval(auction: Pick<AdminAuction, 'status'> | undefined): number | false {
  return auction?.status === 'live' ? LIVE_POLL_MS : false;
}

/** Anti-sniping moved the end: `endsAt` carries the extensions, `originalEndsAt` does not. */
export function extensionMs(auction: Pick<AdminAuction, 'endsAt' | 'originalEndsAt'>): number {
  return Math.max(0, Date.parse(auction.endsAt) - Date.parse(auction.originalEndsAt));
}

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** What is left until `target`, or null once it has passed. */
export function countdown(target: string, now: number): Countdown | null {
  const left = Date.parse(target) - now;
  if (!Number.isFinite(left) || left <= 0) return null;
  const totalSeconds = Math.floor(left / 1000);
  return {
    days: Math.floor(totalSeconds / 86_400),
    hours: Math.floor((totalSeconds % 86_400) / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60,
  };
}

/** Removing a bid takes its automatic answers with it; proxies are never removed on their own. */
export function canRetract(
  auction: Pick<AdminAuction, 'status'>,
  bid: Pick<AdminAuctionBid, 'kind' | 'retractedAt'>,
): boolean {
  return auction.status === 'live' && bid.kind !== 'proxy' && bid.retractedAt === null;
}

/** The scheduled or live auction a store product is a lot in, if any. */
export function activeAuctionOf(items: readonly AdminAuction[]): AdminAuction | null {
  return items.find((auction) => isActive(auction.status)) ?? null;
}
